/**
 * Menu Management Page
 * @description 팀별 Entry 메뉴 그룹·메뉴 show/hide 설정
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import './tenant-admin.css';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';
import { dataService } from '@/services/dataService';
import { PanelLeft, Save, Loader2 } from 'lucide-react';

type ServiceMode = 'all' | 'allowlist' | 'denylist';

interface CategoryPolicy {
  visible: boolean;
  serviceMode?: ServiceMode;
  services?: string[];
}

interface TeamPolicy {
  mode: 'allow-all' | 'custom';
  description?: string;
  categories?: Record<string, CategoryPolicy>;
}

interface MenuConfig {
  schemaVersion?: string;
  tenantId?: string;
  defaults?: {
    unlistedTeamMode?: string;
    multiTeamMerge?: string;
    entryHideMode?: string;
  };
  teamPolicies?: Record<string, TeamPolicy>;
}

interface CatalogCategory {
  categoryKey: string;
  displayName: string;
  services: Array<{ serviceId: string; name: string; description?: string }>;
}

interface TeamRow {
  teamId: string;
  teamName: string;
  status?: string;
}

function emptyCustomCategories(categories: CatalogCategory[]): Record<string, CategoryPolicy> {
  const out: Record<string, CategoryPolicy> = {};
  for (const cat of categories) {
    out[cat.categoryKey] = { visible: false, serviceMode: 'all', services: [] };
  }
  return out;
}

export function MenuManagementPage() {
  const { user } = useAuthStore();
  const tenantId = (user as any)?.tenantId as string | undefined;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [config, setConfig] = useState<MenuConfig | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const [menuConfig, catalog] = await Promise.all([
        dataService.getTenantMenuConfig(tenantId),
        dataService.getTenantMenuCatalog(tenantId),
      ]);
      setConfig(menuConfig);
      setTeams(catalog.teams || []);
      setCategories(catalog.categories || []);
      const first = catalog.teams?.[0]?.teamId || 'admin';
      setSelectedTeamId((prev) => prev || first);
    } catch (err: any) {
      setError(err?.message || '메뉴 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedPolicy: TeamPolicy = useMemo(() => {
    const raw = config?.teamPolicies?.[selectedTeamId];
    if (selectedTeamId === 'admin' || raw?.mode === 'allow-all') {
      return { mode: 'allow-all', description: raw?.description || '관리자 — 모든 메뉴 표시' };
    }
    if (raw?.mode === 'custom') {
      return {
        mode: 'custom',
        description: raw.description,
        categories: { ...emptyCustomCategories(categories), ...(raw.categories || {}) },
      };
    }
    return {
      mode: 'custom',
      description: '미설정 — 아래에서 정책을 구성하세요',
      categories: emptyCustomCategories(categories),
    };
  }, [config, selectedTeamId, categories]);

  const isAllowAll = selectedPolicy.mode === 'allow-all' || selectedTeamId === 'admin';

  const updateSelectedPolicy = (next: TeamPolicy) => {
    setConfig((prev) => {
      const base = prev || { teamPolicies: {}, defaults: {} };
      return {
        ...base,
        teamPolicies: {
          ...(base.teamPolicies || {}),
          [selectedTeamId]: next,
        },
      };
    });
  };

  const setCategoryVisible = (categoryKey: string, visible: boolean) => {
    const cats = { ...(selectedPolicy.categories || {}) };
    cats[categoryKey] = {
      ...(cats[categoryKey] || { serviceMode: 'all', services: [] }),
      visible,
      serviceMode: cats[categoryKey]?.serviceMode || 'all',
      services: cats[categoryKey]?.services || [],
    };
    updateSelectedPolicy({ mode: 'custom', description: selectedPolicy.description, categories: cats });
  };

  const setServiceMode = (categoryKey: string, serviceMode: ServiceMode) => {
    const cats = { ...(selectedPolicy.categories || {}) };
    cats[categoryKey] = {
      ...(cats[categoryKey] || { visible: true, services: [] }),
      visible: cats[categoryKey]?.visible ?? true,
      serviceMode,
      services: cats[categoryKey]?.services || [],
    };
    updateSelectedPolicy({ mode: 'custom', description: selectedPolicy.description, categories: cats });
  };

  const toggleService = (categoryKey: string, serviceId: string, checked: boolean) => {
    const cats = { ...(selectedPolicy.categories || {}) };
    const current = cats[categoryKey] || { visible: true, serviceMode: 'allowlist' as ServiceMode, services: [] };
    const set = new Set(current.services || []);
    if (checked) set.add(serviceId);
    else set.delete(serviceId);
    cats[categoryKey] = {
      ...current,
      visible: true,
      serviceMode: current.serviceMode === 'all' ? 'allowlist' : current.serviceMode || 'allowlist',
      services: [...set],
    };
    updateSelectedPolicy({ mode: 'custom', description: selectedPolicy.description, categories: cats });
  };

  const handleSave = async () => {
    if (!tenantId || !config) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const teamPolicies = { ...(config.teamPolicies || {}) };
      // ensure admin allow-all
      teamPolicies.admin = {
        mode: 'allow-all',
        description: teamPolicies.admin?.description || '관리자 — 모든 메뉴 그룹·메뉴 표시',
      };
      const payload = {
        ...config,
        teamPolicies,
        defaults: {
          unlistedTeamMode: 'show-all-if-permitted',
          multiTeamMerge: 'union',
          entryHideMode: 'remove',
          ...(config.defaults || {}),
        },
      };
      const saved = await dataService.updateTenantMenuConfig(tenantId, payload);
      // PUT 응답에 success/message가 섞여도 설정 객체만 유지
      const {
        success: _s,
        message: _m,
        error: _e,
        ...menuOnly
      } = (saved || {}) as Record<string, unknown>;
      setConfig({
        schemaVersion: (menuOnly.schemaVersion as string) || payload.schemaVersion,
        tenantId: (menuOnly.tenantId as string) || tenantId,
        generatedAt: menuOnly.generatedAt as string | undefined,
        defaults: (menuOnly.defaults as MenuConfig['defaults']) || payload.defaults,
        teamPolicies: (menuOnly.teamPolicies as MenuConfig['teamPolicies']) || payload.teamPolicies,
      });
      setSuccess(
        '메뉴 설정을 저장했습니다. Entry(/entry)로 이동하거나 새로고침하면 즉시 반영됩니다. 검증은 해당 팀(teamIds) 사용자로 로그인하세요. (admin·팀 미배정 계정은 항상 전체 표시)',
      );
    } catch (err: any) {
      setError(err?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!tenantId) {
    return (
      <div className="tenant-admin-page p-6">
        <p className="text-muted-foreground">테넌트 정보가 없습니다.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="tenant-admin-page p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> 로딩 중…
      </div>
    );
  }

  return (
    <div className="tenant-admin-page p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <PanelLeft className="h-6 w-6" />
            메뉴관리
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            팀별로 Entry 좌측 메뉴·우측 서비스 카드의 메뉴 그룹/메뉴 표시를 설정합니다.
            메뉴 그룹 이름은 ITOps Admin(Portal 서비스 관리)의 라벨과 같습니다. 접근제어(view)와 AND로 적용됩니다.
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          저장
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}
      {success && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{success}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-3 p-4 space-y-2">
          <h2 className="text-lg font-medium">팀</h2>
          <div className="space-y-1">
            {teams.map((t) => (
              <button
                key={t.teamId}
                type="button"
                onClick={() => setSelectedTeamId(t.teamId)}
                className={`w-full text-left rounded-md px-3 py-2 text-sm border ${
                  selectedTeamId === t.teamId
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent hover:bg-muted/50'
                }`}
              >
                <div className="font-medium">{t.teamName || t.teamId}</div>
                <div className="text-xs text-muted-foreground">{t.teamId}</div>
              </button>
            ))}
            {teams.length === 0 && (
              <p className="text-sm text-muted-foreground">조직에 팀이 없습니다.</p>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-9 p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-medium">정책</h2>
            {isAllowAll && <Badge variant="secondary">전체 허용</Badge>}
          </div>

          {isAllowAll ? (
            <p className="text-sm text-muted-foreground">
              관리자 팀은 모든 메뉴 그룹과 소속 메뉴가 Entry에 표시됩니다. (변경 불가)
            </p>
          ) : (
            <div className="space-y-4">
              {categories.map((cat) => {
                const pol = selectedPolicy.categories?.[cat.categoryKey] || {
                  visible: false,
                  serviceMode: 'all' as ServiceMode,
                  services: [],
                };
                const mode = (pol.serviceMode || 'all') as ServiceMode;
                return (
                  <div key={cat.categoryKey} className="rounded-lg border p-3 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2 min-w-[10rem]">
                        <Checkbox
                          id={`cat-${cat.categoryKey}`}
                          checked={!!pol.visible}
                          onCheckedChange={(v) => setCategoryVisible(cat.categoryKey, v === true)}
                        />
                        <Label htmlFor={`cat-${cat.categoryKey}`} className="font-medium">
                          {cat.displayName}
                        </Label>
                      </div>
                      {pol.visible && (
                        <Select
                          value={mode}
                          onValueChange={(v) => setServiceMode(cat.categoryKey, v as ServiceMode)}
                        >
                          <SelectTrigger className="w-[10rem] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">모든 메뉴</SelectItem>
                            <SelectItem value="allowlist">선택 메뉴만</SelectItem>
                            <SelectItem value="denylist">제외 메뉴</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {pol.visible && mode !== 'all' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-6">
                        {cat.services.map((svc) => {
                          const checked = (pol.services || []).includes(svc.serviceId);
                          return (
                            <label
                              key={svc.serviceId}
                              className="flex items-center gap-2 text-sm rounded-md px-2 py-1 hover:bg-muted/40"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(v) =>
                                  toggleService(cat.categoryKey, svc.serviceId, v === true)
                                }
                              />
                              <span>{svc.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {pol.visible && mode === 'all' && (
                      <p className="text-xs text-muted-foreground pl-6">
                        {cat.services.map((s) => s.name).join(', ') || '서비스 없음'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
